import produce from 'immer';
import { ActionStatus } from 'thunkless';
import _ from 'lodash';

import { InboxSortOrder } from '@/constants/inbox-sort-order';
import { TicketStatusOption } from '@/constants/ticket-status-option.enum';
import { TicketChannelOption } from '@/constants/ticket-channel-option.enum';

import { EventElementType } from '@/constants/event-element-type.enum';
import { AppAction } from '../actions';
import { EventTypes } from '../actions/event';
import { InboxTypes } from '../actions/inbox';
import { SettingsTypes } from '../actions/settings';
import { TicketTypes } from '../actions/ticket';

export interface InboxState {
  loadingStatus: ActionStatus;
  userLoadingStatus: ActionStatus;
  lastKey: string;
  hasNextPage: boolean;
  tickets: Record<string, string[]>;
  users: Record<string, any>;
  hiddenTickets: Record<string, string[]>;
  filters: {
    [key in 'search'|'userId'|'unanswered'|'unaddressed'|'archived'|'from'|'to']?: string;
  }&{
    status?: TicketStatusOption;
    channel?: TicketChannelOption
    order?: InboxSortOrder;
  };
  paused: boolean;
  pausedStatus: ActionStatus;
  zoomedInMedia: { url: string, mime: string, dimensions?: { w: number, h: number } };
}

export const initialState: InboxState = {
  loadingStatus: null,
  userLoadingStatus: null,
  lastKey: null,
  hasNextPage: null,
  users: {},
  tickets: {},
  hiddenTickets: {},
  filters: {},
  paused: false,
  pausedStatus: ActionStatus.SUCCESS,
  zoomedInMedia: null,
};

const toggle = (value: boolean) => !value;
export const reducer = produce((state: InboxState, action: AppAction) => {
  switch (action.type) {
    case SettingsTypes.SET_SETTINGS: {
      const { user } = action.payload;
      const { pauseInbox } = user || {};
      state.paused = pauseInbox;
      break;
    }

    case SettingsTypes.TOGGLE_PAUSE_INBOX_REQUEST: {
      state.pausedStatus = ActionStatus.BUSY;
      _.update(state, ['paused'], toggle);
      break;
    }
    case SettingsTypes.TOGGLE_PAUSE_INBOX_SUCCESS: {
      state.pausedStatus = ActionStatus.SUCCESS;
      break;
    }
    case SettingsTypes.TOGGLE_PAUSE_INBOX_FAILURE: {
      state.pausedStatus = ActionStatus.FAILURE;
      _.update(state, ['paused'], toggle);
      break;
    }

    case EventTypes.APPEND_EVENTS: {
      action.payload.forEach(({ ticket, element }) => {
        const {
          uuid,
          status: ticketStatus,
          owner_hash_id: ticketUserId,
          channel: ticketChannel,
          unaddressed: ticketUnaddressed,
          activity_at_timestamp: ticketActivityAt,
          account_subdomain: subdomain,
          samePreview,
          insertIndex,
        } = ticket;
        const { direction } = element;

        if (state.paused) return; // skip appending ticket if it's busted
        if (samePreview) return;

        const tickets = state.tickets[subdomain] ?? [];
        if (insertIndex) {
          tickets.splice(insertIndex, 0, uuid);
          return;
        }

        const prevIx = tickets.findIndex((tuuid) => tuuid === uuid);
        const {
          search, status, userId, channel, unanswered, unaddressed, from, to, order,
        } = state.filters;

        if (prevIx < 0 && search) return;
        if (status && status !== ticketStatus) return;
        if (userId && userId !== ticketUserId) return;
        if (channel && channel !== ticketChannel) return;
        if (unanswered === 'on' && direction !== 'in') return;
        if (unaddressed === 'on' && !ticketUnaddressed) return;
        if (from && new Date(ticketActivityAt) <= new Date(from)) return;
        if (to && new Date(ticketActivityAt) >= new Date(to)) return;
        if (element.element_type !== EventElementType.SMS
          && element.element_type !== EventElementType.MMS
          && element.element_type !== EventElementType.CALL
        ) return;

        if (prevIx >= 0) tickets.splice(prevIx, 1);
        if (order === InboxSortOrder.OLDEST || order === InboxSortOrder.LONGEST_WAIT_TIME) {
          tickets.push(uuid);
        } else {
          tickets.unshift(uuid);
        }
        state.tickets[subdomain] = tickets;
      });
      break;
    }

    case InboxTypes.FETCH_INBOX_TICKETS_REQUEST: {
      const { subdomain, limit, after, ...filters } = action.meta;
      state.loadingStatus = ActionStatus.BUSY;
      if (!after || after !== state.lastKey || !_.isEqual(filters, state.filters)) {
        // Start from the beginning
        state.tickets[subdomain] = [];
        state.hiddenTickets[subdomain] = [];
        state.hasNextPage = null;
        state.filters = filters as Record<string, string>;
      }
      break;
    }

    case InboxTypes.FETCH_INBOX_TICKETS_SUCCESS: {
      const { data, lastKey } = action.payload;
      const { subdomain } = action.meta;

      // Perform extra sorting and de-duping if Elasticsearch messed it up
      const sortedData = state.filters.order === InboxSortOrder.LONGEST_WAIT_TIME
        ? data // If sorted by longest wait time don't double-check; not enough data
        : data.sort(
          state.filters.order === InboxSortOrder.OLDEST
            ? (a, b) => (a.last_activity?.sent_at - b.last_activity?.sent_at) || 0
            : (a, b) => (b.last_activity?.sent_at - a.last_activity?.sent_at) || 0,
        );
      const newTickets = _.sortedUniq<string>(sortedData.map(({ uuid }) => uuid));

      if (action.meta.after === state.lastKey) {
        _.update(state.tickets, [subdomain], (tickets) => tickets.concat(newTickets));
      } else {
        state.tickets[subdomain] = newTickets;
      }

      state.lastKey = lastKey;
      state.hasNextPage = !!lastKey;
      state.loadingStatus = ActionStatus.SUCCESS;
      break;
    }

    case InboxTypes.FETCH_INBOX_TICKETS_FAILURE: {
      state.loadingStatus = ActionStatus.FAILURE;
      break;
    }

    case InboxTypes.FETCH_INBOX_USERS_REQUEST: {
      state.userLoadingStatus = ActionStatus.BUSY;
      break;
    }

    case InboxTypes.FETCH_INBOX_USERS_SUCCESS: {
      const { subdomain } = action.meta;
      state.users[subdomain] = state.users[subdomain] || {};

      const { users } = action.payload.data;
      users.forEach((user) => {
        state.users[subdomain][user.id] = user;
      });
      state.userLoadingStatus = ActionStatus.SUCCESS;
      break;
    }

    case InboxTypes.FETCH_INBOX_USERS_FAILURE: {
      state.userLoadingStatus = ActionStatus.FAILURE;
      break;
    }

    case TicketTypes.SET_UNADDRESSED_SUCCESS: {
      const { ticket, unaddressed, subdomain } = action.meta;
      if (state.filters.unaddressed === 'on') {
        if (!unaddressed) {
          _.update(
            state.hiddenTickets,
            [subdomain],
            (hiddenTickets = []) => hiddenTickets.concat(ticket),
          );
        } else _.remove(state.hiddenTickets[subdomain], (item) => item === ticket);
      }
      break;
    }

    case TicketTypes.SET_STATUS_SUCCESS: {
      const { subdomain, ticket, newStatus, currentStatus } = action.meta;
      const { status, archived } = state.filters;
      if (status) {
        if (newStatus !== status) {
          _.update(
            state.hiddenTickets,
            [subdomain],
            (hiddenTickets = []) => hiddenTickets.concat(ticket),
          );
        } else _.remove(state.hiddenTickets[subdomain], (item) => item === ticket);
      } else if (archived !== 'on') {
        if (newStatus === 'Closed') {
          _.update(
            state.hiddenTickets,
            [subdomain],
            (hiddenTickets = []) => hiddenTickets.concat(ticket),
          );
        } else if (currentStatus === 'Closed') _.remove(state.hiddenTickets[subdomain], (item) => item === ticket);
      }
      break;
    }

    case InboxTypes.TOGGLE_ZOOMED_IN_MEDIA_MODAL: {
      state.zoomedInMedia = action.payload;
      break;
    }
  }
}, initialState);
